import React from 'react';
import { CheckCircle, AlertCircle, AlertTriangle } from 'lucide-react';
import S3ServerAccessLogsDetails from './S3ServerAccessLogsDetails';

const TestResources = ({ testDetails, testId }) => {
  // Helper function to determine region status display
  const getRegionStatusDisplay = (details, testId) => {
    // If no resources were checked, consider it compliant
    if (details.resources_checked === 0) {
      return {
        className: 'bg-green-100 text-green-800',
        text: 'Compliant (No Resources)'
      };
    }
    
    // Otherwise, use the status from the details
    if (details.status === 'passed') {
      return {
        className: 'bg-green-100 text-green-800',
        text: 'All Compliant'
      };
    } else if (details.status === 'warning') {
      return {
        className: 'bg-yellow-100 text-yellow-800',
        text: 'Partially Compliant'
      };
    } else {
      return {
        className: 'bg-red-100 text-red-800',
        text: 'Non-Compliant'
      };
    }
  };

  // Helper function to render the appropriate resource details based on test ID
  const renderResourceDetails = () => {
    if (!testDetails) return null;

    // VPC Flow Logs
    if (testId === 'vpc-flow-logs') {
      return renderVpcFlowLogs();
    }
    // CloudTrail
    else if (testId === 'cloud-trail') {
      return renderCloudTrail();
    }
    // Multi-Region CloudTrail
    else if (testId === 'multi-region-cloud-trail') {
      return renderMultiRegionCloudTrail();
    }
    // Vulnerability Scanning
    else if (testId === 'vulnerability-scanning') {
      return renderVulnerabilityScanning();
    }
    // S3 Server Access Logs
    else if (testId === 's3-server-access-logs') {
      return renderS3ServerAccessLogs();
    }
    // S3 Bucket Encryption
    else if (testId === 's3-bucket-encryption') {
      return renderS3BucketEncryption();
    }
    // DynamoDB Encryption
    else if (testId === 'dynamodb-encryption') {
      return renderDynamoDBEncryption();
    }
    // EBS Encryption
    else if (testId === 'ebs-encryption') {
      return renderEbsEncryption();
    }
    // HTTPS Redirect
    else if (testId === 'https-redirect') {
      return renderHttpsRedirect();
    }
    // GuardDuty
    else if (testId === 'guard-duty') {
      return renderGuardDuty();
    }
    // Log Retention
    else if (testId === 'log-retention') {
      return renderLogRetention();
    }
    // MFA on IAM users
    else if (testId === 'mfa-iam-users') {
      return renderMfaIamUsers();
    }
    // MFA on AWS Organizations Users
    else if (testId === 'mfa-org-users') {
      return renderMfaOrgUsers();
    }
    // IAM User Policies
    else if (testId === 'iam-user-policies') {
      return renderIamUserPolicies();
    }
    // Default fallback
    else {
      return (
        <div className="text-gray-700">
          <p>No specific resource details available for this test.</p>
        </div>
      );
    }
  };

  // Render VPC Flow Logs details
  const renderVpcFlowLogs = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.vpc_details && details.vpc_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">VPC Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VPC ID</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flow Logs Enabled</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.vpc_details.map((vpc) => (
                            <tr key={vpc.vpc_id}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{vpc.vpc_id}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {vpc.has_flow_logs ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No VPCs found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render CloudTrail details
  const renderCloudTrail = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.trail_details && details.trail_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">CloudTrail Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trail Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Multi-Region</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logging</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.trail_details.map((trail) => (
                            <tr key={trail.name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{trail.name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {trail.is_multi_region_trail ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {trail.is_logging ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No CloudTrail trails found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render Multi-Region CloudTrail details
  const renderMultiRegionCloudTrail = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.trail_details && details.trail_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">CloudTrail Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trail Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Multi-Region</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logging</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.trail_details.map((trail) => (
                            <tr key={trail.name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{trail.name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {trail.is_multi_region_trail ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {trail.is_logging ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No CloudTrail trails found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render Vulnerability Scanning details
  const renderVulnerabilityScanning = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.repository_details && details.repository_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">ECR Repositories</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Repository Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scan On Push</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scan Frequency</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.repository_details.map((repo) => (
                            <tr key={repo.repository_name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{repo.repository_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {repo.scan_on_push ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {repo.scan_frequency}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No ECR repositories found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render S3 Server Access Logs details
  const renderS3ServerAccessLogs = () => {
    return <S3ServerAccessLogsDetails testDetails={testDetails} testId={testId} getRegionStatusDisplay={getRegionStatusDisplay} />;
  };

  // Render Log Retention details
  const renderLogRetention = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.log_group_details && details.log_group_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">CloudWatch Log Group Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Log Group Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retention Period (Days)</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.log_group_details.map((logGroup, idx) => (
                            <tr key={`${logGroup.log_group_name}-${idx}`}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{logGroup.log_group_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {logGroup.retention_in_days || 'Never Expire'}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {logGroup.has_retention ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Compliant
                                  </span>
                                ) : (
                                  <span className="text-red-600">Non-Compliant</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No CloudWatch Log Groups found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render MFA on IAM users details
  const renderMfaIamUsers = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => {
            // Determine the status for display
            // For MFA IAM users, if there are no users with console access, it should show as compliant
            const displayStatus = details.users_with_console_access === 0 ? 'passed' : (details.status || 'failed');
            
            return (
              <div key={region} className="border border-gray-200 rounded-md mb-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                  <h5 className="font-medium">Region: {region}</h5>
                  <span className={`px-2 py-1 rounded-md text-sm ${
                    displayStatus === 'passed' ? 'bg-green-100 text-green-800' : 
                    displayStatus === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {displayStatus === 'passed' ? 'All Compliant' : 
                     displayStatus === 'warning' ? 'Partially Compliant' : 
                     'Non-Compliant'}
                  </span>
                </div>
                <div className="p-3">
                  <p className="mb-2">{details.message}</p>
                  {details.user_details && details.user_details.length > 0 ? (
                    <div className="mt-3">
                      <h6 className="font-medium mb-2">IAM User Details</h6>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Console Access</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MFA Enabled</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {details.user_details.map((user) => (
                              <tr key={user.username}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{user.username}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {user.has_console_access ? (
                                    <span className="text-blue-600 flex items-center">
                                      <CheckCircle size={16} className="mr-1" /> Yes
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">No</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {user.has_mfa ? (
                                    <span className="text-green-600 flex items-center">
                                      <CheckCircle size={16} className="mr-1" /> Yes
                                    </span>
                                  ) : user.has_console_access ? (
                                    <span className="text-red-600">No</span>
                                  ) : (
                                    <span className="text-gray-500">N/A (No console access)</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No IAM users found in this region</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Render MFA on AWS Organizations Users details
  const renderMfaOrgUsers = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => {
            // Determine the status for display
            // For MFA Org users, if there are no users with console access, it should show as compliant
            const displayStatus = details.users_with_console_access === 0 ? 'passed' : (details.status || 'failed');
            
            return (
              <div key={region} className="border border-gray-200 rounded-md mb-3">
                <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                  <h5 className="font-medium">Region: {region}</h5>
                  <span className={`px-2 py-1 rounded-md text-sm ${
                    displayStatus === 'passed' ? 'bg-green-100 text-green-800' : 
                    displayStatus === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                    'bg-red-100 text-red-800'
                  }`}>
                    {displayStatus === 'passed' ? 'All Compliant' : 
                     displayStatus === 'warning' ? 'Partially Compliant' : 
                     'Non-Compliant'}
                  </span>
                </div>
                <div className="p-3">
                  <p className="mb-2">{details.message}</p>
                  
                  {details.accounts_in_organization && (
                    <div className="mb-3">
                      <h6 className="font-medium mb-2">Organization Information</h6>
                      <p className="text-gray-700">
                        Total accounts in organization: {details.accounts_in_organization}
                      </p>
                    </div>
                  )}
                  
                  {details.user_details && details.user_details.length > 0 ? (
                    <div className="mt-3">
                      <h6 className="font-medium mb-2">Organization User Details</h6>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Console Access</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MFA Enabled</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {details.user_details.map((user) => (
                              <tr key={`${user.account_id}-${user.username}`}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{user.username}</td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                  {user.account_name || user.account_id}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {user.has_console_access ? (
                                    <span className="text-blue-600 flex items-center">
                                      <CheckCircle size={16} className="mr-1" /> Yes
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">No</span>
                                  )}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm">
                                  {user.has_mfa ? (
                                    <span className="text-green-600 flex items-center">
                                      <CheckCircle size={16} className="mr-1" /> Yes
                                    </span>
                                  ) : user.has_console_access ? (
                                    <span className="text-red-600">No</span>
                                  ) : (
                                    <span className="text-gray-500">N/A (No console access)</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No Organization users found in this region</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Render IAM User Policies details
  const renderIamUserPolicies = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${
                  details.status === 'passed' ? 'bg-green-100 text-green-800' : 
                  details.status === 'warning' ? 'bg-yellow-100 text-yellow-800' : 
                  'bg-red-100 text-red-800'
                }`}>
                  {details.status === 'passed' ? 'All Compliant' : 
                   details.status === 'warning' ? 'Partially Compliant' : 
                   'Non-Compliant'}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.user_details && details.user_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">IAM User Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Has Policies</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Managed Policies</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inline Policies</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.user_details.map((user) => (
                            <tr key={user.user_name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{user.user_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {user.has_policies ? (
                                  <span className="text-red-600">Yes</span>
                                ) : (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> No
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700">
                                {user.managed_policies && user.managed_policies.length > 0 ? (
                                  <ul className="list-disc pl-5">
                                    {user.managed_policies.map((policy, idx) => (
                                      <li key={`${user.user_name}-managed-${idx}`}>{policy}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-700">
                                {user.inline_policies && user.inline_policies.length > 0 ? (
                                  <ul className="list-disc pl-5">
                                    {user.inline_policies.map((policy, idx) => (
                                      <li key={`${user.user_name}-inline-${idx}`}>{policy}</li>
                                    ))}
                                  </ul>
                                ) : (
                                  <span className="text-gray-500">None</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No IAM users found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render S3 Bucket Encryption details
  const renderS3BucketEncryption = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.bucket_details && details.bucket_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">S3 Bucket Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bucket Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Enabled</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Type</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.bucket_details.map((bucket) => (
                            <tr key={bucket.bucket_name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{bucket.bucket_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {bucket.has_encryption ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {bucket.encryption_type || 'DEFAULT'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No S3 buckets found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render DynamoDB Encryption details
  const renderDynamoDBEncryption = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${
                  // For DynamoDB encryption, all regions should be considered compliant
                  // since all DynamoDB tables are encrypted by default
                  testId === 'dynamodb-encryption' ? 'bg-green-100 text-green-800' :
                  getRegionStatusDisplay(details, testId).className
                }`}>
                  {testId === 'dynamodb-encryption' ? 'All Compliant' :
                   getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.table_details && details.table_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">DynamoDB Table Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Table Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Enabled</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Type</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.table_details.map((table) => (
                            <tr key={table.table_name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{table.table_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {table.has_encryption ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {table.encryption_type || 'DEFAULT'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No DynamoDB tables found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render EBS Encryption details
  const renderEbsEncryption = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${
                  // For EBS encryption, if no volumes are found, consider it compliant
                  (details.resources_checked === 0) ? 'bg-green-100 text-green-800' :
                  getRegionStatusDisplay(details, testId).className
                }`}>
                  {(details.resources_checked === 0) ? 'Compliant (No Volumes)' :
                   getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.volume_details && details.volume_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">EBS Volume Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Volume ID</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Enabled</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Encryption Type</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.volume_details.map((volume) => (
                            <tr key={volume.volume_id}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{volume.volume_id}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {volume.is_encrypted ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">
                                {volume.kms_key_id || 'None'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No EBS volumes found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render HTTPS Redirect details
  const renderHttpsRedirect = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.load_balancer_details && details.load_balancer_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">Load Balancer Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Load Balancer Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">HTTPS Redirect Enabled</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.load_balancer_details.map((loadBalancer) => (
                            <tr key={loadBalancer.load_balancer_name}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{loadBalancer.load_balancer_name}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {loadBalancer.has_https_redirect ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No Load Balancers found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  // Render GuardDuty details
  const renderGuardDuty = () => {
    const message = testDetails.message || '';
    const regionsChecked = testDetails.regions_checked || [];
    const regionDetails = testDetails.region_details || {};

    return (
      <>
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
          <p className="text-blue-700">{message}</p>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Regions Checked</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {regionsChecked.map((region) => (
              <div key={region} className="bg-gray-50 rounded-md p-2 text-sm">
                {region}
              </div>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="font-medium mb-2">Region Details</h4>
          {Object.entries(regionDetails).map(([region, details]) => (
            <div key={region} className="border border-gray-200 rounded-md mb-3">
              <div className="flex justify-between items-center p-3 bg-gray-50 border-b border-gray-200">
                <h5 className="font-medium">Region: {region}</h5>
                <span className={`px-2 py-1 rounded-md text-sm ${getRegionStatusDisplay(details, testId).className}`}>
                  {getRegionStatusDisplay(details, testId).text}
                </span>
              </div>
              <div className="p-3">
                <p className="mb-2">{details.message}</p>
                {details.detector_details && details.detector_details.length > 0 ? (
                  <div className="mt-3">
                    <h6 className="font-medium mb-2">GuardDuty Detector Details</h6>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detector ID</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Enabled</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {details.detector_details.map((detector) => (
                            <tr key={detector.detector_id}>
                              <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-700">{detector.detector_id}</td>
                              <td className="px-3 py-2 whitespace-nowrap text-sm">
                                {detector.is_enabled ? (
                                  <span className="text-green-600 flex items-center">
                                    <CheckCircle size={16} className="mr-1" /> Yes
                                  </span>
                                ) : (
                                  <span className="text-red-600">No</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No GuardDuty detectors found in this region</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="p-4 border-t border-gray-200">
      <h3 className="text-lg font-medium mb-3">Resources Detected</h3>
      
      <div className="bg-white rounded-md">
        {testDetails ? (
          <>
            <div className="mb-4">
              <h4 className="font-medium mb-2">Summary</h4>
              <p className="text-gray-700">{testDetails.message}</p>
            </div>
            
            {renderResourceDetails()}
          </>
        ) : (
          <p className="text-gray-500">No resource details available.</p>
        )}
      </div>
    </div>
  );
};

export default TestResources;
